import React, {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'
import {toAbsoluteUrl} from '../../../_metronic/helpers'

const initialValues = {
  newPassword: '',
}

// Mirrors the rules enforced server-side by ActivateProjectGuestCommandValidator
const validationScheme = Yup.object().shape({
  newPassword: Yup.string()
    .required('Password is required')
    .min(16, 'Please choose a minimum of 16 characters')
    .matches(
      /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])/,
      'The password must contain at least one uppercase, one lowercase, one digit, and one symbol'
    )
})

export function ProjectGuestActivate() {
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState('')
  const [success, setSuccess] = useState(false)

  const params: any = useParams()

  useEffect(() => {
    setSuccess(false)
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: validationScheme,
    onSubmit: async (values) => {
      setHasErrors(undefined)
      const response = await ProjectGuestPortalClient.Activate(params.code, values.newPassword)

      if (!response.successful) {
        setHasErrors(true)
        console.error(response)
        if (response.validationErrors && response.validationErrors.length > 0) {
          setErrorMessage(response.validationErrors[0].message || 'An error occurred while activating your account.');
        } else {
          setErrorMessage(response.message || 'An error occurred while activating your account.');
        }
      } else {
        setHasErrors(false)
        setSuccess(true)
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'>
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_project_guest_activate_form'
            onSubmit={formik.handleSubmit}
          >
            <div className='text-center mb-10'>
              <img
                alt='Logo'
                className='h-150px logo mb-5'
                src={toAbsoluteUrl('/media/logos/daysee_logo.png')}
              />
              <h1 className='text-dark mb-3'>Set your password</h1>
              <div className='text-gray-400 fw-bold fs-4'>Choose a password to activate your project guest account</div>
            </div>

            {hasErrors === true && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{errorMessage}</div>
              </div>
            )}
            {success && (
              <div className='mb-10 bg-light-info p-8 rounded'>
                <div className='text-info text-center'>
                  Account activated. <br />
                  <Link to='/projects/guest/login' className='link-primary fw-bolder'>
                    Log in here
                  </Link>
                </div>
              </div>
            )}

            {!success && (
              <>
                <div className='fv-row mb-10'>
                  <label className='form-label fw-bolder text-gray-900 fs-6'>New Password</label>
                  <input
                    type='password'
                    placeholder=''
                    autoComplete='off'
                    {...formik.getFieldProps('newPassword')}
                    className={clsx(
                      'form-control form-control-lg form-control-solid',
                      {'is-invalid': formik.touched.newPassword && formik.errors.newPassword},
                      {
                        'is-valid': formik.touched.newPassword && !formik.errors.newPassword,
                      }
                    )}
                  />
                  {formik.touched.newPassword && formik.errors.newPassword && (
                    <div className='fv-plugins-message-container text-danger'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.newPassword}</span>
                      </div>
                    </div>
                  )}
                  <ul className='text-muted fs-7 mt-2 mb-0 ps-4'>
                    <li>At least 16 characters</li>
                    <li>At least one uppercase letter</li>
                    <li>At least one lowercase letter</li>
                    <li>At least one digit</li>
                    <li>At least one symbol</li>
                  </ul>
                </div>

                <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                  <button
                    type='submit'
                    id='kt_project_guest_activate_submit'
                    className='btn btn-lg btn-primary fw-bolder me-4'
                  >
                    {!formik.isSubmitting && <span className='indicator-label'>Activate account</span>}
                    {formik.isSubmitting && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        Please wait...
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}
